import React, { useState, useEffect } from "react";
import {
  Drawer,
  Avatar,
  Badge,
  Box,
  Typography,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Divider,
  Button,
  useTheme,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import WhiteBJSEnglish1 from "../../images/WhiteBJSEnglish1.svg";
import { Outlet } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NewspaperIcon from "@mui/icons-material/Newspaper";

import NotificationBell from "./NotificationBell";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearUserData } from "../../features/user/UserSlices";
import { useSelector } from "react-redux";
import CustomFooter from "./Footer";
import { can, canModule } from "../../permissions/can";
import { useNotifications } from "../../hooks/useNotifications";
import { usePermission } from "../../permissions/usePermission";
import { isSuperAdmin as isSuperAdminHelper } from "../../permissions/isSuperAdmin";
import { extractDesignationLabels } from "../../utils/designationUtils";
import { getPublicS3Url } from "../../utils/s3PublicUrl";
import ApiCallService from "../../Service/ApiCallService";

import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import XIcon from "@mui/icons-material/X";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const resolvePermissionsObject = (state) => {
  const payload = state?.permissions?.permissions;
  if (!payload || typeof payload !== "object") return {};
  if (payload.permissions && typeof payload.permissions === "object") {
    return payload.permissions;
  }
  return payload;
};

const derivePermissionFromKey = (permissionKey, permissions) => {
  if (!permissionKey || typeof permissionKey !== "string") return null;

  const normalized = permissionKey.toUpperCase();
  const parts = normalized.split("_").filter(Boolean);
  if (parts.length < 2) return null;

  const moduleKeys = Object.keys(permissions || {})
    .filter((key) => key && key !== "*")
    .sort((a, b) => b.length - a.length);

  const matchedModule = moduleKeys.find((moduleKey) => {
    const moduleParts = moduleKey.split("_").filter(Boolean);
    if (moduleParts.length >= parts.length) return false;
    return moduleParts.every((value, idx) => value === parts[idx]);
  });

  if (!matchedModule) return null;

  const modulePartLength = matchedModule.split("_").filter(Boolean).length;
  const remainingParts = parts.slice(modulePartLength);
  if (remainingParts.length < 2) return null;

  const moduleNode = permissions?.[matchedModule] || {};
  const subModuleKeys = Object.keys(moduleNode || {})
    .filter((key) => key && key !== "*" && key !== "all")
    .sort((a, b) => b.length - a.length);

  const matchedSubModule = subModuleKeys.find((subModuleKey) => {
    const subModuleParts = subModuleKey.split("_").filter(Boolean);
    if (subModuleParts.length >= remainingParts.length) return false;
    return subModuleParts.every((value, idx) => value === remainingParts[idx]);
  });

  if (!matchedSubModule) {
    const fallbackAction =
      remainingParts[remainingParts.length - 1]?.toLowerCase();
    const fallbackSubModule = remainingParts.slice(0, -1).join("_");
    if (!fallbackSubModule || !fallbackAction) return null;
    return {
      module: matchedModule,
      subModule: fallbackSubModule,
      action: fallbackAction,
    };
  }

  const subModulePartLength = matchedSubModule
    .split("_")
    .filter(Boolean).length;
  const action = remainingParts
    .slice(subModulePartLength)
    .join("_")
    .toLowerCase();
  if (!action) return null;

  return { module: matchedModule, subModule: matchedSubModule, action };
};

const Sidebar2 = ({ menuItems, logo }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isProduction = process.env.REACT_APP_ENV === "production";
  // STATES
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [user, setUser] = useState({
    userFirstName: "",
    userLastName: "",
    profilePicUrl: "",
    userLocation: "",
    emailId: "",
  });
  const userDetails = useSelector((state) => state.user.userDetails);
  const permissionsState = useSelector((state) => state.permissions);
  const { isSuperAdmin: hasGlobalAccess } = usePermission();
  const designationLabels = extractDesignationLabels(permissionsState);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [communityUnreadCount, setCommunityUnreadCount] = useState(0);
  const toDesignationSlug = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const preferredDesignation = hasGlobalAccess
    ? "superuser"
    : designationLabels[0] || "member";
  const activeDesignationSlug = hasGlobalAccess
    ? "superuser"
    : localStorage.getItem("activeDesignationSlug") ||
    toDesignationSlug(preferredDesignation);
  const toDesignationPath = (path) => {
    if (!path || typeof path !== "string") return path;
    if (!path.startsWith("/")) return path;
    if (!activeDesignationSlug) return path;
    if (path.startsWith(`/${activeDesignationSlug}/`)) return path;
    const nextPath = `/${activeDesignationSlug}${path}`;
    console.debug("[Nav] designation path", {
      from: path,
      to: nextPath,
      slug: activeDesignationSlug,
    });
    return nextPath;
  };
  const adminModules = Array.isArray(userDetails?.adminModules)
    ? userDetails.adminModules
    : [];
  const adminDesignationLabels = [];
  if (adminModules.includes("NETWORK")) {
    adminDesignationLabels.push("Network Admin");
  }
  if (adminModules.includes("PROGRAM")) {
    adminDesignationLabels.push("Program Admin");
  }
  const adminDesignationText =
    adminDesignationLabels.length > 0
      ? adminDesignationLabels.join(", ")
      : "Admin";
  const normalizeRole = (role) =>
    String(role || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
  const roleKey = normalizeRole(userDetails?.userRole);
  const superRoleLabel =
    userDetails?.userRoleLabel ||
    (roleKey === "superadmin"
      ? "Super Admin"
      : roleKey === "superuser"
        ? "Super User"
        : roleKey === "headoffice"
          ? "Head Office"
          : null);
  const designationText = superRoleLabel
    ? superRoleLabel
    : userDetails?.userRole === "Admin"
      ? adminDesignationText
      : designationLabels.length > 0
        ? designationLabels.join(", ")
        : "-";

  // SOCIAL LINKS
  const socialLinks = [
    {
      href: "https://www.facebook.com/BJSIndia",
      icon: <FacebookIcon fontSize="medium" />,
      label: "Facebook",
    },
    {
      href: "https://www.instagram.com/bhartiyajainsanghatana/",
      icon: <InstagramIcon fontSize="medium" />,
      label: "Instagram",
    },
    {
      href: "https://www.youtube.com/channel/UCFLe91tMq_twtQ7EDi97USQ",
      icon: <YouTubeIcon fontSize="medium" />,
      label: "YouTube",
    },
    {
      href: "https://x.com/BJS_India",
      icon: <XIcon fontSize="medium" />,
      label: "X",
    },
    {
      href: "https://www.linkedin.com/company/bjs-india/posts/?feedView=all",
      icon: <LinkedInIcon fontSize="medium" />,
      label: "LinkedIn",
    },
  ];

  useEffect(() => {
    if (userDetails) {
      setUser({
        userFirstName: userDetails.userFirstName || "",
        userLastName: userDetails.userLastName || "",
        profilePicUrl: getPublicS3Url(userDetails.userImageURL) || "",
        userLocation: userDetails.regionName || "",
        emailId: userDetails.emailId || "",
      });
    }
  }, [userDetails]);

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname, location.search, location.key]);

  const permissions = useSelector(resolvePermissionsObject);
  const accessToken = localStorage.getItem("accessToken") || userDetails?.accessToken;
  const {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
  } = useNotifications(accessToken);

  // Helper function to check if user has permission
  const hasPermission = (requiredPermission) => {
    if (!requiredPermission) return false;

    // support array permissions (OR condition)
    if (Array.isArray(requiredPermission)) {
      return requiredPermission.some(hasPermission);
    }

    if (typeof requiredPermission === "string") {
      const derived = derivePermissionFromKey(requiredPermission, permissions);
      if (derived) {
        return can(
          permissions,
          derived.module,
          derived.subModule,
          derived.action,
        );
      }

      // Parent nav items may intentionally use module-only keys (e.g. USER_MANAGEMENT).
      return canModule(permissions, requiredPermission);
    }

    const { module, subModule, action } = requiredPermission;

    return can(permissions, module, subModule, action);
  };

  // Filter menu items based on permissions
  const filterMenuItems = (items) => {
    if (!Array.isArray(items)) return [];

    const isSuperAdminUser =
      hasGlobalAccess || isSuperAdminHelper(userDetails);

    return items
      .map((item) => {
        // Users item should be visible ONLY to SuperAdmin
        if (
          item.key === "users" ||
          item.label === "Users" ||
          item.superAdminOnly
        ) {
          if (!isSuperAdminUser) {
            return null;
          }
        }

        const filteredChildren = item.children
          ? filterMenuItems(item.children)
          : [];

        const hasItemPermission = item.permission
          ? hasPermission(item.permission)
          : true;

        if (hasItemPermission || filteredChildren.length > 0) {
          return {
            ...item,
            children:
              filteredChildren.length > 0 ? filteredChildren : undefined,
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  const filteredMenuItems = filterMenuItems(menuItems);

  // FUNCTIONS
  const logRouteAccess = (item, resolvedPath) => {
    console.debug("[Nav] click", {
      label: item?.label,
      link: item?.link,
      resolvedPath,
      permission: item?.permission,
    });
  };
  const toggleSubMenu = (key) => setOpenMenu(openMenu === key ? null : key);
  const handleDrawerOpen = () => setIsDrawerOpen(true);
  const handleDrawerClose = () => setIsDrawerOpen(false);

  const handleProfileClick = (event) => setProfileAnchor(event.currentTarget);
  const handleProfileClose = () => setProfileAnchor(null);

  const handleLogout = () => {
    localStorage.clear();
    dispatch(clearUserData());
    navigate("/");
  };

  const goToProfile = () => {
    const userId = localStorage.getItem("userId");
    setIsDrawerOpen(false);
    navigate(toDesignationPath(`/profile/${userId}`), {
      state: { mode: "view", navigateFrom: "viewAccount" },
    });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 60,
          px: { xs: 1, sm: 3 },
          background: "#000",
          // background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.figma} 100%)`,
          boxShadow: theme.shadows[2],
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={handleDrawerOpen}
            sx={{ color: theme.palette.secondary.main, mr: 1 }}
          >
            <MenuIcon fontSize="large" />
          </IconButton>

          <Box
            component="img"
            // src={logo || require("../../images/bjsLogoWhiteBG.png")}
            src={WhiteBJSEnglish1}
            alt="Logo"
            sx={{
              maxHeight: 38,
              ml: 1,
              cursor: "pointer",
              transition: "transform 0.2s",
              "&:hover": { transform: "scale(1.05)" },
            }}
            onClick={() => {
              setIsDrawerOpen(false);
              navigate(`/dashboard`);
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "left", sm: "center" },
            gap: { xs: 1, sm: 1, md: 2, lg: 2 },
          }}
        >

          {!isProduction &&

            <Tooltip title="Community Activity">
              <IconButton
                aria-label="Community Activity"
                onClick={() => navigate(toDesignationPath("/community-activity"))}
                sx={{
                  color: "#fff",
                  width: 40,
                  height: 40,
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.08)",
                  "&:hover": { background: "rgba(255,255,255,0.16)", transform: "translateY(-1px)" },
                  transition: "all 0.2s ease",
                }}
              >
                <Badge
                  badgeContent={communityUnreadCount > 0 ? communityUnreadCount : null}
                  color="secondary"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontWeight: 800,
                      animation: communityUnreadCount > 0 ? "communityBadgePop 1.8s ease-in-out infinite" : "none",
                    },
                    "@keyframes communityBadgePop": {
                      "0%, 100%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.1)" },
                    },
                  }}
                >
                  <NewspaperIcon />
                </Badge>
              </IconButton>
            </Tooltip>


          }



          <Tooltip title="Notifications">
            <IconButton
              aria-label="Notifications"
              sx={{
                color: "#fff",
                width: 40,
                height: 40,
                border: "1px solid rgba(255,255,255,0.25)",
                background: "rgba(255,255,255,0.08)",
                "&:hover": { background: "rgba(255,255,255,0.16)", transform: "translateY(-1px)" },
                transition: "all 0.2s ease",
              }}

            >
              {/* <NotificationBell
                notifications={notifications}
                unreadCount={unreadCount}
                loading={loading}
                error={error}
                onRetry={fetchNotifications}
                onMarkAllRead={markAllNotificationsRead}
                onMarkRead={markNotificationRead}
              /> */}




              <NotificationBell
                notifications={notifications}
                unreadCount={unreadCount}
                loading={loading}
                error={error}
                onRetry={fetchNotifications}
                onMarkRead={markNotificationRead}
                onMarkAllRead={markAllNotificationsRead}
              />






            </IconButton>
          </Tooltip>

          <IconButton sx={{ p: 0.5 }} onClick={handleProfileClick}>
            <Avatar
              src={user.profilePicUrl}
              sx={{
                width: 38,
                height: 38,
                bgcolor: theme.palette.secondary.main,
                color: theme.palette.primary.main,
                fontWeight: 600,
              }}
            >
              {user.userFirstName ? (
                user.userFirstName.charAt(0).toUpperCase()
              ) : (
                <PersonOutlineIcon />
              )}
            </Avatar>
          </IconButton>

          <Popover
            open={Boolean(profileAnchor)}
            anchorEl={profileAnchor}
            onClose={handleProfileClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                mt: 1.2,
                width: 280,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 2 }}>
              <Avatar
                src={user.profilePicUrl}
                sx={{
                  width: 48,
                  height: 48,
                  mr: 2,
                  bgcolor: theme.palette.secondary.main,
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                }}
              >
                {user.userFirstName ? (
                  user.userFirstName.charAt(0).toUpperCase()
                ) : (
                  <PersonOutlineIcon />
                )}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {user.userFirstName} {user.userLastName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ wordBreak: "break-word" }}
                >
                  {user.emailId}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: "pointer", mt: 0.5 }}
                >
                  {designationText}
                </Typography>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={goToProfile}
                    sx={{ textTransform: "none", borderRadius: 1.5 }}
                  >
                    View Account
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setLogoutDialogOpen(true)}
                    sx={{ textTransform: "none", borderRadius: 1.5 }}
                  >
                    Logout
                  </Button>

                  <Dialog
                    open={logoutDialogOpen}
                    onClose={() => setLogoutDialogOpen(false)}
                  >
                    <DialogTitle>Confirm Logout</DialogTitle>
                    <DialogContent>
                      Are you sure you want to logout?
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setLogoutDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        variant="outlined"
                        sx={{ textTransform: "none", borderRadius: 1.5 }}
                        onClick={() => {
                          setLogoutDialogOpen(false);
                          handleLogout();
                        }}
                      >
                        Logout
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Box>
              </Box>
            </Box>
            <Divider />
          </Popover>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 270,
            background: "#7b1fa2",
            color: "white",
            borderRight: "2px solid grey",
          },
        }}
      >
        <Box sx={{ position: "relative", height: "100%", py: 4 }}>
          <IconButton
            onClick={handleDrawerClose}
            sx={{ position: "absolute", right: 8, top: 2, color: "white" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <List sx={{ p: 1 }}>
            {filteredMenuItems.map((item) => (
              <Box key={item.key}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (item.children) {
                        toggleSubMenu(item.key);
                      } else {
                        setIsDrawerOpen(false);
                        const rawLink =
                          typeof item.link === "string"
                            ? item.link.trim()
                            : item.link;
                        const resolved = toDesignationPath(rawLink);
                        logRouteAccess(item, resolved);
                        navigate(resolved);
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      background:
                        location.pathname === item.link ||
                          location.pathname === toDesignationPath(item.link)
                          ? "rgba(255,255,255,0.3)"
                          : "transparent",
                      "&:hover": { background: "rgba(255,255,255,0.2)" },
                      transition: "0.25s",
                    }}
                  >
                    {item.icon && (
                      <ListItemIcon sx={{ color: "white" }}>
                        {item.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 500 }}>
                          {item.label}
                        </Typography>
                      }
                    />
                    {item.children &&
                      (openMenu === item.key ? <ExpandLess /> : <ExpandMore />)}
                  </ListItemButton>
                </ListItem>

                {item.children && (
                  <Collapse
                    in={openMenu === item.key}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List sx={{ pl: 4 }}>
                      {item.children.map((sub) => (
                        <ListItemButton
                          key={sub.key}
                          onClick={() => {
                            setIsDrawerOpen(false);
                            const rawLink =
                              typeof sub.link === "string"
                                ? sub.link.trim()
                                : sub.link;
                            const resolved = toDesignationPath(rawLink);
                            logRouteAccess(sub, resolved);
                            const isCreateRoute =
                              typeof resolved === "string" &&
                              resolved.includes("/committee/registerNewUser");

                            navigate(resolved, {
                              state: isCreateRoute ? { mode: "create" } : {},
                            });
                          }}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            "&:hover": { background: "rgba(255,255,255,0.2)" },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography sx={{ fontSize: 14 }}>
                                {sub.label}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
              </Box>
            ))}
          </List>

          <Box sx={{ px: 2, mt: "auto" }}>
            <Divider sx={{ mb: 2, opacity: 0.3 }} />
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  color: "white",
                  background: "rgba(255,0,0,0.25)",
                  "&:hover": { background: "rgba(255,0,0,0.35)" },
                }}
              >
                <ListItemIcon sx={{ color: "white" }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </Box>
        </Box>
      </Drawer>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <main className="content" style={{ flex: 1 }}>
          <Outlet />
        </main>
        <footer>
          <CustomFooter />
        </footer>
      </div>
    </>
  );
};

export default Sidebar2;
